<template>
  <div class="dashboard-view">
    <!-- Page Header -->
    <div class="mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold text-primary-text dark:text-primary-dark-text mb-2">
        Dashboard
      </h1>
      <p class="text-secondary-text dark:text-secondary-dark-text">
        Welcome back! Here's an overview of your account and activities.
      </p>
    </div>

    <!-- Loading/Error States -->
    <LoadingState 
      :is-loading="loading" 
      type="dashboard" 
      text="Loading dashboard..." 
    />
    
    <div
      v-if="!loading && error"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <div class="flex items-center">
        <XCircle class="w-5 h-5 text-red-500 mr-2" />
        <span class="text-red-700 dark:text-red-300">{{ error }}</span>
      </div>
      <button
        @click="fetchDashboardData"
        class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
      >
        Try again
      </button>
    </div>

    <!-- Content -->
    <div v-if="!loading && !error">
      <!-- Stats Cards (Admin only) -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
        <!-- Total Employees -->
        <div class="card p-4 sm:p-6 relative flex flex-col min-h-[140px]">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <Users class="w-8 h-8 text-blue-500" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt
                  class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text truncate"
                >
                  Total Employees
                </dt>
                <dd class="text-lg font-medium text-primary-text dark:text-primary-dark-text">
                  {{ stats.totalEmployees || 0 }}
                </dd>
              </dl>
            </div>
          </div>
          
          <!-- Stats positioned at bottom left -->
          <div class="mt-auto pt-4">
            <div class="flex flex-wrap gap-2">
              <div
                class="flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-3 py-1.5 border border-green-200 dark:border-green-800"
              >
                <CheckCircle class="w-4 h-4 text-green-600 dark:text-green-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-green-700 dark:text-green-300 font-medium mr-1">Active:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.activeEmployees || 0
                }}</span>
              </div>
              <div class="flex items-center rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-1.5 border border-red-200 dark:border-red-800">
                <XCircle class="w-4 h-4 text-red-600 dark:text-red-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-red-700 dark:text-red-300 font-medium mr-1">Inactive:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.inactiveEmployees || 0
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Module Progress -->
        <div class="card p-4 sm:p-6 relative flex flex-col min-h-[140px]">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <ClipboardList class="w-8 h-8 text-purple-500" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt
                  class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text truncate"
                >
                  Module Progress
                </dt>
                <dd class="text-lg font-medium text-primary-text dark:text-primary-dark-text">
                  {{ stats.totalModuleAssignments || 0 }}
                </dd>
              </dl>
            </div>
          </div>
          
          <!-- Stats positioned at bottom left -->
          <div class="mt-auto pt-4">
            <div class="flex flex-wrap gap-2">
              <div
                class="flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-3 py-1.5 border border-green-200 dark:border-green-800"
              >
                <CheckCircle class="w-4 h-4 text-green-600 dark:text-green-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-green-700 dark:text-green-300 font-medium mr-1">Completed:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.completedModuleAssignments || 0
                }}</span>
              </div>
              <div class="flex items-center rounded-md bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 border border-blue-200 dark:border-blue-800">
                <Clock class="w-4 h-4 text-blue-600 dark:text-blue-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-blue-700 dark:text-blue-300 font-medium mr-1">In Progress:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.inProgressModuleAssignments || 0
                }}</span>
              </div>
              <div
                class="flex items-center rounded-md bg-gray-50 dark:bg-gray-900/20 px-3 py-1.5 border border-gray-200 dark:border-gray-800"
              >
                <XCircle class="w-4 h-4 text-gray-600 dark:text-gray-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-gray-700 dark:text-gray-300 font-medium mr-1">Not Started:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.notStartedModuleAssignments || 0
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Total Exams -->
        <div class="card p-4 sm:p-6 relative flex flex-col min-h-[140px]">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <Archive class="w-8 h-8 text-green-500" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt
                  class="text-sm font-medium text-secondary-text dark:text-secondary-dark-text truncate"
                >
                  Total Exams
                </dt>
                <dd class="text-lg font-medium text-primary-text dark:text-primary-dark-text">
                  {{ stats.totalModules || 0 }}
                </dd>
              </dl>
            </div>
          </div>
          
          <!-- Stats positioned at bottom left -->
          <div class="mt-auto pt-4">
            <div class="flex flex-wrap gap-2">
              <div
                class="flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-3 py-1.5 border border-green-200 dark:border-green-800"
              >
                <CheckCircle class="w-4 h-4 text-green-600 dark:text-green-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-green-700 dark:text-green-300 font-medium mr-1">Passed:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.passedExams || 0
                }}</span>
              </div>
              <div class="flex items-center rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-1.5 border border-red-200 dark:border-red-800">
                <XCircle class="w-4 h-4 text-red-600 dark:text-red-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-red-700 dark:text-red-300 font-medium mr-1">Failed:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.failedExams || 0
                }}</span>
              </div>
              <div
                class="flex items-center rounded-md bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1.5 border border-yellow-200 dark:border-yellow-800"
              >
                <Clock class="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-1.5 flex-shrink-0" />
                <span class="text-xs text-yellow-700 dark:text-yellow-300 font-medium mr-1">Skipped:</span>
                <span class="text-xs text-primary-text dark:text-primary-dark-text font-semibold">{{
                  stats.skippedExams || 0
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Welcome Section (Admin only options) -->
      <div class="card p-6 mb-8">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-semibold text-primary-text dark:text-primary-dark-text mb-2">
              Welcome, {{ userName }}!
            </h2>
            <p class="text-secondary-text dark:text-secondary-dark-text">
              You're logged in as an Admin. Here's what you can do:
            </p>
          </div>
          <div class="hidden md:block">
            <LayoutDashboard class="w-16 h-16 text-primary/20" />
          </div>
        </div>
        <div class="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Admin Options -->
          <router-link
            to="/employee"
            class="flex items-center p-4 bg-primary-bg/10 dark:bg-primary-dark-bg/10 rounded-lg hover:shadow transition-shadow"
            aria-label="Manage Employees"
          >
            <Users class="w-6 h-6 text-primary mr-3" />
            <div>
              <h3 class="font-medium text-primary-text dark:text-primary-dark-text">
                Manage Employees
              </h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                Add and manage employees
              </p>
            </div>
          </router-link>

          <router-link
            to="/module"
            class="flex items-center p-4 bg-primary-bg/10 dark:bg-primary-dark-bg/10 rounded-lg hover:shadow transition-shadow"
            aria-label="Manage Modules"
          >
            <Archive class="w-6 h-6 text-primary mr-3" />
            <div>
              <h3 class="font-medium text-primary-text dark:text-primary-dark-text">
                Manage Modules
              </h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                Create and manage training materials
              </p>
            </div>
          </router-link>

          <router-link
            to="/exam"
            class="flex items-center p-4 bg-primary-bg/10 dark:bg-primary-dark-bg/10 rounded-lg hover:shadow transition-shadow"
            aria-label="Manage Exams"
          >
            <ClipboardList class="w-6 h-6 text-primary mr-3" />
            <div>
              <h3 class="font-medium text-primary-text dark:text-primary-dark-text">Manage Exams</h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                Create and manage assessments
              </p>
            </div>
          </router-link>

          <button
            type="button"
            @click="navigateToCertificates"
            class="flex items-center p-4 bg-primary-bg/10 dark:bg-primary-dark-bg/10 rounded-lg hover:shadow transition-shadow w-full text-left"
            aria-label="Manage Certificates"
          >
            <Award class="w-6 h-6 text-primary mr-3" />
            <div>
              <h3 class="font-medium text-primary-text dark:text-primary-dark-text">
                Manage Certificates
              </h3>
              <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                Issue and manage certifications
              </p>
            </div>
          </button>
        </div>
      </div>

  <!-- Bottom Row -->
  <div class="flex flex-col stack-until-1665 gap-6">
        <!-- Recent Activities -->
        <div class="card p-6 flex flex-col flex-1 h-[1120px]">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-primary-text dark:text-primary-dark-text flex items-center gap-2">
              Recent Activities
              <!-- Unread count badge -->
              <span 
                v-if="unreadActivitiesCount > 0" 
                class="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500 text-white"
                :title="`${unreadActivitiesCount} unread activit${unreadActivitiesCount > 1 ? 'ies' : 'y'}`"
              >
                {{ unreadActivitiesCount }}
              </span>
            </h2>
            <div class="flex items-center gap-2">
              <!-- Mark all as read button -->
              <button
                v-if="unreadActivitiesCount > 0"
                @click="handleMarkAllAsRead"
                class="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Mark all as read"
              >
                Mark all read
              </button>
              <!-- Refresh button -->
              <button
                @click="handleRefreshActivities"
                class="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Refresh activities"
              >
                <RefreshCw class="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </button>
            </div>
          </div>
          <div v-if="displayActivities.length === 0" class="flex-1 flex items-center justify-center">
            <div class="text-center py-8">
              <FileText class="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p class="text-secondary-text dark:text-secondary-dark-text">
                No recent activities
              </p>
            </div>
          </div>
          <div v-else class="flex-1 max-h-[70rem] overflow-y-auto space-y-3 pr-2 recent-activities-container pt-3">
            <div
              v-for="activity in displayActivities"
              :key="activity.id"
              @click="handleActivityClick(activity)"
              :class="[
                // add right padding so absolutely-positioned status badge doesn't overlap text
                'activity-item flex items-start gap-3 p-4 pr-12 rounded-lg shadow-sm border transition-all group cursor-pointer relative',
                activity.isRead
                  ? 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600'
                  : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 border-l-4 border-l-blue-500'
              ]"
            >
              <!-- Top-right status badge -->
              <!-- Show badge when a status exists OR when this is a module assignment (default to 'assigned') -->
              <div v-if="(activity.status || activity.type === 'module_assignment' || activity.type === 'user_registration') && (activity.type === 'exam_result' || activity.type === 'module_start' || activity.type === 'module_completion' || activity.type === 'module_assignment' || activity.type === 'user_registration')"
                   class="activity-status-badge absolute top-3 right-3 z-10">
                <span :class="[ 'flex items-center px-2 py-0.5 rounded-full text-xs font-medium', getStatusBadgeClass(getActivityStatus(activity)) ]">
                  {{ capitalizeStatus(getActivityStatus(activity)) }}
                </span>
              </div>
              <!-- New indicator -->
              <div 
                v-if="activity.isNew && !activity.isRead" 
                class="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"
                title="New"
              ></div>
              
              <!-- Activity Icon -->
              <div class="activity-icon flex-shrink-0">
                <div 
                  :class="[
                    'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                    getActivityIconBg(activity.type, activity.status)
                  ]"
                >
                  <component 
                    :is="getActivityIcon(activity.type, activity.status)" 
                    :class="['w-5 h-5', getActivityIconColor(activity.type, activity.status)]"
                  />
                </div>
              </div>

              <!-- Activity Content -->
              <div class="activity-content flex-1 min-w-0">
                <!-- Header: Name -->
                <div class="flex items-start justify-between gap-2 mb-1.5">
                  <div class="flex-1 min-w-0">
                    <p class="activity-name font-semibold text-primary-text dark:text-primary-dark-text text-sm leading-tight">
                      <template v-if="activity.first_name && activity.last_name">
                        {{ activity.first_name }} {{ activity.last_name }}
                      </template>
                      <template v-else>
                        {{ extractEmail(activity.description) }}
                      </template>
                    </p>
                  </div>
                </div>

                <!-- Email -->
                <p class="activity-email text-xs text-secondary-text dark:text-secondary-dark-text mt-0.5 truncate">
                  {{ activity.email || extractEmail(activity.description) }}
                </p>

                <!-- Time Row (status moved to top-right) -->
                <div class="flex items-center gap-2 mt-1.5 mb-2">
                  <!-- Time -->
                  <span class="activity-time flex-shrink-0 text-xs text-secondary-text dark:text-secondary-dark-text flex items-center gap-1">
                    <Clock class="w-3.5 h-3.5" />
                    {{ formatDate(activity.timestamp) }}
                  </span>
                </div>

                <!-- Activity Description -->
                <div class="activity-description flex items-start gap-2">
                  <p class="text-sm text-secondary-text dark:text-secondary-dark-text leading-relaxed">
                    {{ getActivityDescription(activity) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Module Rotation Calendar (Admin Only) -->
        <div class="card p-6 flex-1 flex flex-col">
          <ModuleRotationCalendar class="flex-1" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { dashboardApi, type DashboardStats, type Activity } from '@/services/dashboard'
import ModuleRotationCalendar from '@/components/ModuleRotationCalendar.vue'
import { LoadingState } from '@/components/common'
import { useActivityPolling, type ActivityWithReadStatus } from '@/composables/notifications/useActivityPolling'
import {
  XCircle,
  Users,
  Archive,
  CheckCircle,
  Clock,
  LayoutDashboard,
  FileText,
  Award,
  ClipboardList,
  UserPlus,
  CalendarCheck,
  AlertCircle,
  PlayCircle,
  RefreshCw
} from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Empathy Health Services, Inc.')
useTitle(`Dashboard - ${appTitle.value}`)

// Composables
const { userName } = useUser()

const router = useRouter()

// State
const loading = ref(true)
const error = ref<string | null>(null)
const stats = ref<DashboardStats>({
  totalModules: 0,
  totalEmployees: 0,
  totalExams: 0,
  passedExams: 0,
  failedExams: 0,
  skippedExams: 0,
  totalModuleAssignments: 0,
  completedModuleAssignments: 0,
  inProgressModuleAssignments: 0,
  notStartedModuleAssignments: 0,
  currentExams: 0,
  upcomingExams: 0,
  activeEmployees: 0,
  inactiveEmployees: 0
})
const recentActivity = ref<Activity[]>([])

// Activity polling with notifications
const {
  activities: polledActivities,
  unreadActivitiesCount,
  clearNewActivitiesCount,
  markAsRead,
  markAllAsRead,
  refresh: refreshActivities
} = useActivityPolling({
  enableToasts: false,
  autoStart: true,
  isAdmin: true
})

// Use polled activities as the source of truth
const displayActivities = computed<ActivityWithReadStatus[]>(() => {
  if (polledActivities.value.length > 0) {
    return polledActivities.value
  }
  // Fallback to recentActivity with default read status
  return recentActivity.value.map(activity => ({
    ...activity,
    isRead: false,
    isNew: false
  }))
})

// Methods
const fetchDashboardData = async () => {
  try {
    loading.value = true
    error.value = null

    // Fetch dashboard statistics
    const dashboardStats = await dashboardApi.getStats()
    stats.value = dashboardStats

    // Fetch recent activity
    const activity = await dashboardApi.getRecentActivity()
    recentActivity.value = activity
  } catch (err) {
    error.value = 'Failed to load dashboard data. Please try again.'
    console.error('Dashboard data fetch error:', err)
  } finally {
    loading.value = false
  }
}

const formatDate = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

  if (diffInHours < 1) {
    return 'Just now'
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
  } else {
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
  }
}

function extractEmail(description: string): string {
  // Match email pattern in the description
  const match = description.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/)
  return match ? match[0] : ''
}

/**
 * Get the appropriate icon for the activity type
 */
function getActivityIcon(type: string, status?: string) {
  switch (type) {
    case 'user_registration':
      return UserPlus
    case 'module_assignment':
      return CalendarCheck
    case 'exam_result':
      return status === 'passed' ? Award : AlertCircle
    case 'module_start':
      return PlayCircle
    case 'module_completion':
      return CheckCircle
    default:
      return FileText
  }
}

/**
 * Get the icon background color classes
 */
function getActivityIconBg(type: string, status?: string) {
  switch (type) {
    case 'user_registration':
      return 'bg-blue-100 dark:bg-blue-900/30'
    case 'module_assignment':
      return 'bg-purple-100 dark:bg-purple-900/30'
    case 'exam_result':
      return status === 'passed' 
        ? 'bg-green-100 dark:bg-green-900/30' 
        : 'bg-red-100 dark:bg-red-900/30'
    case 'module_start':
      return 'bg-purple-100 dark:bg-purple-900/30'
    case 'module_completion':
      return 'bg-blue-100 dark:bg-blue-900/30'
    default:
      return 'bg-gray-100 dark:bg-gray-900/30'
  }
}

/**
 * Get the icon color classes
 */
function getActivityIconColor(type: string, status?: string) {
  switch (type) {
    case 'user_registration':
      return 'text-blue-600 dark:text-blue-400'
    case 'module_assignment':
      return 'text-purple-600 dark:text-purple-400'
    case 'exam_result':
      return status === 'passed' 
        ? 'text-green-600 dark:text-green-400' 
        : 'text-red-600 dark:text-red-400'
    case 'module_start':
      return 'text-purple-600 dark:text-purple-400'
    case 'module_completion':
      return 'text-blue-600 dark:text-blue-400'
    default:
      return 'text-gray-600 dark:text-gray-400'
  }
}

/**
 * Get status badge classes
 */
function getStatusBadgeClass(status: string) {
  switch (status) {
    case 'passed':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
    case 'failed':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
    case 'in_progress':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
    case 'completed':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
    case 'assigned':
      return 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800'
    case 'user_registered':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
    default:
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-800'
  }
}

/**
 * Capitalize the first letter of status strings like 'passed'/'failed'
 */
function capitalizeStatus(status: string): string {
  if (!status) return ''
  // Special-case statuses with underscores for prettier display
  if (status === 'in_progress') return 'In Progress'
  if (status === 'user_registered') return 'Registered'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

/**
 * Determine an activity status to display.
 * If the activity has an explicit status use it; for module_assignment default to 'assigned'.
 */
function getActivityStatus(activity: Activity | any): string {
  if (!activity) return ''
  if (activity.status) return activity.status
  if (activity.type === 'module_assignment') return 'assigned'
  if (activity.type === 'user_registration') return 'user_registered'
  return ''
}

/**
 * Get a cleaner activity description
 */
function getActivityDescription(activity: Activity): string {
  const { type, description } = activity
  
  switch (type) {
    case 'user_registration':
      return 'Registered as a new employee'
    case 'module_assignment': {
      // Extract the year and months from description
      const match = description.match(/assigned training for (\d{4}) \((.*?)\)/)
      if (match) {
        return `Assigned training modules for ${match[1]}`
      }
      return 'Assigned training modules'
    }
    case 'exam_result': {
      // Extract module name from description
      const moduleMatch = description.match(/exam for (.+)$/)
      if (moduleMatch) {
        return `Completed exam: ${moduleMatch[1]}`
      }
      return 'Completed an exam'
    }
    default:
      return description
  }
}

/**
 * Navigate to certificates route if it exists, otherwise fallback to profile.
 */
function navigateToCertificates() {
  const hasCertificatesRoute = router.getRoutes().some(r => r.name === 'certificates' || r.path === '/certificates')
  if (hasCertificatesRoute) {
    router.push({ name: 'certificates' }).catch(noop)
  } else {
    // Fallback: navigate to profile if specific certificates route not implemented
    router.push({ name: 'Profile' }).catch(noop)
  }
}

/**
 * Handle refresh activities and mark as viewed
 */
const handleRefreshActivities = async () => {
  await refreshActivities()
  clearNewActivitiesCount()
}

// Handle activity click - mark as read
const handleActivityClick = (activity: any) => {
  if (!activity.isRead) {
    markAsRead(activity.id)
  }
}

// Handle mark all as read
const handleMarkAllAsRead = () => {
  markAllAsRead()
  clearNewActivitiesCount()
}

function noop() {
  /* intentionally empty */
}

// Lifecycle
onMounted(() => {
  fetchDashboardData()
})
</script>

<style scoped>
/* Make scrollbar visible and styled for the recent activities list */
.recent-activities-container {
  /* ensure the container creates a new stacking context for correct paint */
  position: relative;
}

/* WebKit-based browsers (Chrome, Edge, Safari) */
.recent-activities-container::-webkit-scrollbar {
  width: 12px;
}
.recent-activities-container::-webkit-scrollbar-track {
  background: transparent;
}
.recent-activities-container::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.12);
  border-radius: 8px;
  border: 3px solid transparent; /* provide padding inside the track */
  background-clip: padding-box;
}
.recent-activities-container:hover::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
}

/* Firefox */
.recent-activities-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.12) transparent;
}

/* Dark mode tweaks */
:deep(.dark) .recent-activities-container::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.08);
}
:deep(.dark) .recent-activities-container:hover::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.14);
}
:deep(.dark) .recent-activities-container {
  scrollbar-color: rgba(255,255,255,0.08) transparent;
}
</style>

<style scoped>
.card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

.dark .card {
  background-color: #1f2937;
  border-color: #374151;
}

.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Activity item responsive styles */
.activity-item {
  transition: all 0.2s ease-in-out;
}

.activity-item:hover {
  transform: translateY(-1px);
}

@media (max-width: 640px) {
  /* Stack items with better spacing on mobile */
  .activity-item {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 0.875rem !important;
  }

  .activity-icon {
    align-self: flex-start;
  }

  .activity-content {
    width: 100%;
  }

  .activity-name {
    font-size: 0.9375rem;
  }

  .activity-description {
    font-size: 0.8125rem;
  }

  /* Show mobile-only elements, hide desktop-only on small screens */
  .mobile-only { 
    display: block; 
  }
  
  .desktop-only { 
    display: none; 
  }
}

/* On larger screens, show desktop-only and hide mobile-only */
@media (min-width: 641px) {
  .mobile-only { 
    display: none; 
  }
  
  .desktop-only { 
    display: inline-flex; 
  }
}

@media (max-width: 640px) {
  .card {
    padding: 1rem;
  }

  .flex-col {
    gap: 0.5rem;
  }

  /* Ensure icons remain visible on small screens */
  .lucide {
    min-width: 1.25rem;
    min-height: 1.25rem;
  }

  /* Improve text readability on small screens */
  .text-xs {
    font-size: 0.75rem;
    line-height: 1rem;
  }
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Top-right status badge styling */
.activity-status-badge span {
  /* add a slight shadow so badge stands out over card */
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
}

@media (max-width: 640px) {
  /* Move badge inside flow on very small screens to avoid overlap */
  .activity-status-badge {
    position: static !important;
    display: inline-block;
    margin-left: 0;
    margin-top: 0.5rem;
  }
}
</style>

<style scoped>
@media (min-width: 1665px) {
  .stack-until-1665 {
    flex-direction: row;
  }
}

/* Pulse animation for new activity badge */
@keyframes pulse-scale {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.animate-pulse {
  animation: pulse-scale 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Smooth transitions for refresh button */
button svg {
  transition: transform 0.3s ease;
}

button:active svg {
  transform: rotate(180deg);
}

/* Read/Unread activity styles */
.activity-item-unread {
  border-left: 4px solid #3b82f6;
}

/* New activity pulse */
@keyframes new-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

/* Smooth click feedback */
.activity-item-clickable:active {
  transform: scale(0.98);
}
</style>
